import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart3, UserCheck } from 'lucide-react';
import { couponsApi } from '../../api/endpoints/coupons';
import { SlideOver } from '../../components/ui/SlideOver';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SarIcon } from '../../components/ui/CurrencyDisplay';
import { formatDate, formatCurrency } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';

interface CouponStatsSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  couponId: string | null;
  storeId: string;
}

export function CouponStatsSlideOver({ isOpen, onClose, couponId, storeId }: CouponStatsSlideOverProps) {
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const navigate = useNavigate();
  const { storeId: routeStoreId } = useParams<{ storeId: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['coupon', storeId, couponId],
    queryFn: () => couponsApi.get(storeId, couponId!),
    enabled: !!storeId && !!couponId && isOpen,
  });

  const coupon = data?.coupon;
  const stats = data?.stats;
  const usage = data?.usage ?? [];

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('coupons.usageStats', 'Coupon Stats')}
      size="md"
    >
      {isLoading || !coupon ? (
        <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
      ) : (
        <div className="space-y-6">
          {/* Coupon header */}
          <div className="rounded-xl border border-border-default bg-bg-tertiary p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <code className="text-lg font-bold text-accent">{coupon.couponId}</code>
              <Badge variant={coupon.discountType === 'percentage' ? 'info' : 'accent'}>
                {coupon.discountType === 'percentage' ? `${coupon.amount}%` : formatCurrency(coupon.amount ?? 0, currentStore?.currency)}
              </Badge>
              <Badge variant={coupon.isActive ? 'success' : 'default'}>
                {coupon.isActive ? t('coupons.active') : t('coupons.inactive')}
              </Badge>
            </div>
            <div className="mt-1 text-[11px] text-text-muted">
              {t('coupons.discountRateLabel', 'الخصم الذي يحصل عليه العميل')}
            </div>
            {coupon.description && <p className="mt-2 text-sm text-text-secondary">{coupon.description}</p>}
          </div>

          {/* Affiliate info panel — only when this is an affiliate coupon.
              Surfaces the *commission rate* (not the discount) so it's
              obvious what the affiliator gets per use, separate from what
              the customer saves. */}
          {coupon.affiliator?.name && (() => {
            const aff = coupon.affiliator!;
            const currency = currentStore?.currency;
            const rateLabel = aff.commissionType === 'percentage'
              ? `${aff.commissionAmount ?? aff.amount ?? 0}%`
              : formatCurrency((aff.commissionAmount ?? aff.amount ?? 0) as number, currency);
            const capValue = aff.maxCommission ?? aff.maximumAmount;
            return (
              <div className="rounded-xl border border-info/30 bg-info/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info/15 text-info">
                    <UserCheck className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-text-primary truncate">{aff.name}</span>
                      <Badge variant="info">
                        {rateLabel} {t('coupons.commissionShort', 'عمولة')}
                      </Badge>
                    </div>
                    <div className="mt-0.5 text-[11px] text-text-muted">
                      {t('coupons.commissionRateLabel', 'العمولة التي تُدفع للمسوّق عن كل عملية')}
                    </div>
                    {aff.email && (
                      <div className="mt-1 text-[11px] text-text-muted truncate" dir="ltr">{aff.email}</div>
                    )}
                    {aff.commissionType === 'percentage' && capValue != null && capValue > 0 && (
                      <div className="mt-1 text-[11px] text-text-muted">
                        {t('coupons.maxCommissionHintShort', 'بحد أقصى')}{' '}
                        <span className="font-semibold text-text-secondary">{formatCurrency(capValue as number, currency)}</span>{' '}
                        {t('coupons.perUse', 'لكل استخدام')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Stats cards */}
          {(() => {
            const hasAffiliate = !!coupon.affiliator?.name;
            const currency = currentStore?.currency;
            const currencySuffix = (!currency || currency === 'SAR')
              ? <> <SarIcon /></>
              : ` ${currency}`;
            return (
              <div className={`grid gap-3 ${hasAffiliate ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
                <StatCard
                  title={t('coupons.totalUses', 'Total Uses')}
                  value={stats?.totalUses ?? coupon.currentUses ?? 0}
                  icon={<BarChart3 className="h-4 w-4" />}
                />
                <StatCard
                  title={t('coupons.totalDiscount', 'Total Discount')}
                  // CurrencyDisplay strips trailing zeros for whole numbers, so we
                  // render the total ourselves with an explicit 2-decimal fixed
                  // format — merchants want consistent .00 / .50 / .25 endings
                  // when scanning stats, not a mix of "100" and "100.5".
                  value={
                    <span>
                      {(stats?.totalDiscount ?? 0).toFixed(2)}
                      {currencySuffix}
                    </span>
                  }
                  icon={<BarChart3 className="h-4 w-4" />}
                />
                {hasAffiliate && (
                  <StatCard
                    title={t('coupons.totalCommission', 'Affiliate Commission')}
                    value={
                      <span>
                        {(stats?.totalCommission ?? 0).toFixed(2)}
                        {currencySuffix}
                      </span>
                    }
                    icon={<BarChart3 className="h-4 w-4" />}
                  />
                )}
              </div>
            );
          })()}

          {/* Usage history */}
          <div>
            <h3 className="text-sm font-semibold mb-3">
              {t('coupons.usageHistory', 'Usage History')}
            </h3>
            {usage.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-6">{t('coupons.noUsage', 'No usage recorded yet')}</p>
            ) : (
              <div className="space-y-2">
                {usage.map((u: any) => {
                  const orderMongoId = u.order?._id;
                  const refLabel = `#${u.order?.referenceId || '-'}`;
                  return (
                  <div key={u._id} className="flex items-center justify-between rounded-lg border border-border-default/50 bg-bg-tertiary px-4 py-3">
                    <div className="flex items-center gap-3">
                      {orderMongoId ? (
                        <button
                          type="button"
                          onClick={() => {
                            const sid = routeStoreId || storeId;
                            navigate(`/${sid}/orders/${orderMongoId}`);
                            onClose();
                          }}
                          className="font-mono text-sm text-accent hover:underline cursor-pointer"
                          title={t('orders.viewOrder', 'فتح الطلب')}
                        >
                          {refLabel}
                        </button>
                      ) : (
                        <span className="font-mono text-sm text-accent">{refLabel}</span>
                      )}
                      {u.customer?.name && <span className="text-sm text-text-secondary">{u.customer.name}</span>}
                    </div>
                    <div className="flex items-center gap-4">
                      {u.discountAmount != null && (
                        <span className="text-sm font-medium">{formatCurrency(u.discountAmount, currentStore?.currency)}</span>
                      )}
                      {u.createdAt && (
                        <span className="text-xs text-text-muted">{formatDate(u.createdAt)}</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </SlideOver>
  );
}
